import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Paper,
    Avatar,
    Chip,
    CircularProgress,
    Tooltip,
    Collapse,
    keyframes,
    useTheme,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    ContentCopy as CopyIcon,
    OpenInFull as MaximizeIcon,
    CloseFullscreen as MinimizeIcon,
    ExpandMore as ExpandMoreIcon,
    Psychology as PsychologyIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import { marked } from 'marked';
import { CustomTextField } from '../common/CustomTextFields';
import { getAISuggestions, processAIQueryStream } from '../../services/aiService';
import { toast } from 'react-toastify';
import { AI_COLORS } from '../../constants/aiColors';
import { API_URL } from '../../config';

// Configure marked for safe, clean rendering
marked.setOptions({
    breaks: true,       // Convert \n to <br>
    gfm: true,          // GitHub-flavored markdown (tables, etc.)
    headerIds: false,
    mangle: false,
});

// Shimmer animation for AI avatar
const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// Fade in animation for messages
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Convert markdown to HTML using the `marked` library.
 * Handles tables, lists, bold/italic, code blocks, links, and line breaks correctly.
 */
const markdownToHtml = (text) => {
    if (!text) return '';
    // Normalise any escaped \n sequences that LLMs sometimes produce
    const normalised = text.replace(/\\n/g, '\n');
    return marked.parse(normalised);
};

const AIAssistant = ({ open, onClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Determine if user has AI chat permission from AuthContext
    const hasAIChatPermission = useMemo(() => {
        return user?.role?.permissions?.ai_chat?.view === true;
    }, [user]);
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'ai',
            text: "Hi! I'm your AI Twin.\n\n**Try asking me basic questions regarding your business:**",
            timestamp: new Date(),
            showSuggestions: true
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [currentProgress, setCurrentProgress] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [expandedReasoning, setExpandedReasoning] = useState({});
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Handle clicks on links within AI messages for client-side navigation
    const handleMessageClick = useCallback((e) => {
        // Check if the clicked element is a link
        const link = e.target.closest('a');
        if (link && link.href) {
            const url = new URL(link.href);
            // Only handle internal links (same origin)
            if (url.origin === window.location.origin) {
                e.preventDefault();

                // API links (like PDF endpoints) should use the API_URL base
                if (url.pathname.startsWith('/api/')) {
                    // Construct the full API URL using the correct backend host/port
                    const apiUrl = `${API_URL}${url.pathname}`;
                    console.log('Opening PDF URL:', apiUrl);
                    window.open(apiUrl, '_blank');
                } else {
                    // Regular frontend routes use React Router navigation
                    onClose(); // Close the AI drawer
                    navigate(url.pathname); // Navigate using React Router
                }
            }
        }
    }, [navigate, onClose]);

    useEffect(() => {
        if (open) {
            loadSuggestions();
            // Focus input when drawer opens
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadSuggestions = async () => {
        try {
            const sugg = await getAISuggestions('general');
            setSuggestions(sugg);
        } catch (error) {
            console.error('Error loading suggestions:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!hasAIChatPermission) {
            toast.error('AI chat is not enabled for your role. Contact your administrator to enable this feature.');
            return;
        }
        if (!inputValue.trim() || isProcessing) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: inputValue,
            timestamp: new Date()
        };

        const queryText = inputValue;
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsProcessing(true);
        setCurrentProgress({ stage: 'started', message: 'Getting answer...', progress: 0 });

        try {
            await processAIQueryStream(
                queryText,
                sessionId,
                (progressData) => setCurrentProgress(progressData),
                (response) => {
                    const aiMessage = {
                        id: Date.now() + 1,
                        type: 'ai',
                        text: response.message,
                        data: response.data,
                        dataType: response.type,
                        reasoning: response.reasoning,
                        timestamp: new Date()
                    };
                    setMessages(prev => [...prev, aiMessage]);
                    setSessionId(response.session_id);
                    setIsProcessing(false);
                    setCurrentProgress(null);
                },
                (error) => {
                    console.error('Error processing query:', error);
                    const errorText = error?.message || "I apologize, but I encountered an error processing your request. Please try again.";
                    const errorMessage = {
                        id: Date.now() + 1,
                        type: 'ai',
                        text: errorText,
                        timestamp: new Date()
                    };
                    setMessages(prev => [...prev, errorMessage]);
                    toast.error(errorText);
                    setIsProcessing(false);
                    setCurrentProgress(null);
                }
            );
        } catch (error) {
            console.error('Error in streaming:', error);
            const errorText = error?.message || "I apologize, but I encountered an error processing your request. Please try again.";
            const errorMessage = {
                id: Date.now() + 1,
                type: 'ai',
                text: errorText,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
            toast.error(errorText);
            setIsProcessing(false);
            setCurrentProgress(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            if (!hasAIChatPermission) {
                e.preventDefault();
                toast.error('AI chat is not enabled for your role. Contact your administrator to enable this feature.');
                return;
            }
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        inputRef.current?.focus();
    };

    const handleCopyMessage = async (text) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                toast.success('Message copied to clipboard');
            } else {
                // Fallback for non-HTTPS or older browsers
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                toast.success('Message copied to clipboard');
            }
        } catch (err) {
            toast.error('Could not copy to clipboard');
        }
    };

    const toggleReasoning = (messageId) => {
        setExpandedReasoning(prev => ({
            ...prev,
            [messageId]: !prev[messageId]
        }));
    };

    const handleClearChat = () => {
        setMessages([
            {
                id: 1,
                type: 'ai',
                text: "Hi! I'm your AI Twin.\n\n**Try asking me basic questions regarding your business:**",
                timestamp: new Date(),
                showSuggestions: true
            }
        ]);
        loadSuggestions();
    };

    const formatTimestamp = (date) => {
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit'
        });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            disableScrollLock={true}
            ModalProps={{
                keepMounted: false,
                BackdropProps: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(2px)'
                    }
                }
            }}
            PaperProps={{
                sx: {
                    width: isMaximized
                        ? { xs: '100%', sm: 'calc(100vw - 48px)' }
                        : { xs: '100%', sm: 480 },
                    height: isMaximized
                        ? 'calc(100vh - 48px)'
                        : 'calc(75vh + 120px)',
                    maxHeight: isMaximized ? 'none' : '900px',
                    position: 'fixed !important',
                    top: '50vh !important',
                    transform: 'translateY(-50%) !important',
                    right: '24px !important',
                    bottom: 'auto !important',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isMaximized ? '16px' : '24px',
                    boxShadow: AI_COLORS.boxShadowDrawer,
                    zIndex: 1200,
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }
            }}
            SlideProps={{
                direction: 'left',
                timeout: 300
            }}
            sx={{
                '& .MuiDrawer-paper': {
                    position: 'fixed !important',
                    top: '50vh !important',
                    transform: 'translateY(-50%) !important'
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 3,
                    background: AI_COLORS.gradient,
                    color: theme.palette.common.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: `0 4px 12px ${AI_COLORS.shadow}`,
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(90deg,
                            transparent 0%,
                            rgba(255, 255, 255, 0.1) 50%,
                            transparent 100%)`,
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 3s ease-in-out infinite`,
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}>
                    <Box
                        component="img"
                        src="/ai-white.svg"
                        alt="Enable AI"
                        sx={{
                            height: 32,
                            width: 32,
                            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))'
                        }}
                    />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.3rem', letterSpacing: '0.5px' }}>
                            Enable AI
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Clear Chat">
                        <IconButton
                            onClick={handleClearChat}
                            sx={{
                                color: 'white',
                                mr: 0.5,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                                    transform: 'rotate(90deg)',
                                    transition: 'all 0.3s'
                                }
                            }}
                            size="small"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isMaximized ? "Minimize" : "Maximize"}>
                        <IconButton
                            onClick={() => setIsMaximized(!isMaximized)}
                            sx={{
                                color: 'white',
                                mr: 0.5,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                                    transform: 'scale(1.1)',
                                    transition: 'all 0.2s'
                                }
                            }}
                            size="small"
                        >
                            {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Messages Area */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    p: 3,
                    background: `linear-gradient(180deg, ${AI_COLORS.bgLight} 0%, ${AI_COLORS.bgLighter} 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5
                }}
            >
                {!hasAIChatPermission && (
                    <Alert
                        severity="error"
                        icon={<LockIcon />}
                        sx={{
                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                            borderColor: '#ef4444',
                            color: '#991b1b',
                            '& .MuiAlert-icon': {
                                color: '#dc2626'
                            }
                        }}
                    >
                        AI chat is not enabled for your role. Contact your administrator to enable this feature.
                    </Alert>
                )}
                {messages.map((message) => (
                    <Box
                        key={message.id}
                        sx={{
                            display: 'flex',
                            justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                            gap: 1
                        }}
                    >
                        {message.type === 'ai' && (
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    background: AI_COLORS.gradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.5,
                                    boxShadow: `0 4px 12px ${AI_COLORS.shadowStrong}`,
                                    border: '2px solid rgba(255, 255, 255, 0.8)',
                                    animation: `${fadeIn} 0.4s ease-out`
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/ai-white.svg"
                                    alt="AI"
                                    sx={{
                                        width: 22,
                                        height: 22
                                    }}
                                />
                            </Box>
                        )}
                        
                        <Box
                            sx={{
                                maxWidth: '80%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    pr: message.type === 'ai' && !message.showSuggestions ? 5 : 2,
                                    bgcolor: message.type === 'user'
                                        ? AI_COLORS.primary
                                        : message.dataType === 'identified_keywords'
                                        ? theme.palette.success.light
                                        : theme.palette.background.paper,
                                    background: message.type === 'user'
                                        ? AI_COLORS.gradient
                                        : message.dataType === 'identified_keywords'
                                        ? `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.light} 100%)`
                                        : theme.palette.background.paper,
                                    color: message.type === 'user' ? theme.palette.common.white : theme.palette.text.primary,
                                    borderRadius: message.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                    position: 'relative',
                                    boxShadow: message.type === 'user'
                                        ? `0 4px 12px ${AI_COLORS.shadow}`
                                        : message.dataType === 'identified_keywords'
                                        ? '0 2px 12px rgba(34, 197, 94, 0.15)'
                                        : '0 2px 12px rgba(0, 0, 0, 0.08)',
                                    border: message.type === 'ai'
                                        ? message.dataType === 'identified_keywords'
                                            ? `1px solid ${theme.palette.success.main}`
                                            : `1px solid ${AI_COLORS.border}`
                                        : 'none',
                                    animation: `${fadeIn} 0.4s ease-out`,
                                    '& strong': {
                                        fontWeight: 600,
                                        color: message.type === 'user'
                                            ? theme.palette.common.white
                                            : message.dataType === 'identified_keywords'
                                            ? theme.palette.success.dark
                                            : AI_COLORS.primaryDark
                                    },
                                    '& p': {
                                        margin: 0
                                    }
                                }}
                            >
                                <Box
                                    onClick={handleMessageClick}
                                    sx={{
                                        wordBreak: 'break-word',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.6,
                                        // marked wraps text in <p> tags — remove top margin on first, bottom on last
                                        '& p': { margin: '0 0 0.5em 0', '&:last-child': { marginBottom: 0 } },
                                        '& ul, & ol': { pl: 2.5, mt: 0.25, mb: 0.5 },
                                        '& li': { mb: 0.25 },
                                        '& strong': { fontWeight: 700 },
                                        '& em': { fontStyle: 'italic' },
                                        '& code': {
                                            background: '#f3f4f6',
                                            px: 0.5,
                                            borderRadius: '3px',
                                            fontFamily: 'monospace',
                                            fontSize: '0.82em',
                                        },
                                        '& pre': {
                                            background: '#f5f5f5',
                                            p: 1.5,
                                            borderRadius: 1,
                                            overflowX: 'auto',
                                            border: '1px solid #e0e0e0',
                                            my: 1,
                                            '& code': { background: 'none', px: 0 }
                                        },
                                        '& blockquote': {
                                            borderLeft: '3px solid #d1d5db',
                                            pl: 1.5,
                                            ml: 0,
                                            color: 'text.secondary',
                                        },
                                        // Table styles
                                        '& table': {
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            fontSize: '0.82rem',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            my: 1.5,
                                            display: 'block',
                                            overflowX: 'auto',
                                        },
                                        '& thead': { background: '#f8f8f8' },
                                        '& th': {
                                            padding: '8px 12px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: '1px solid #e0e0e0',
                                            borderRight: '1px solid #f0f0f0',
                                            whiteSpace: 'nowrap',
                                            '&:last-child': { borderRight: 'none' },
                                        },
                                        '& td': {
                                            padding: '7px 12px',
                                            borderBottom: '1px solid #f5f5f5',
                                            borderRight: '1px solid #f5f5f5',
                                            '&:last-child': { borderRight: 'none' },
                                        },
                                        '& tr:last-child td': { borderBottom: 'none' },
                                        '& tr:hover td': { background: 'rgba(139,92,246,0.03)' },
                                        '& a': {
                                            color: '#2563eb',
                                            textDecoration: 'none',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' }
                                        },
                                        '& h1, & h2, & h3': { mt: 1, mb: 0.5, fontWeight: 700 },
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: markdownToHtml(message.text)
                                    }}
                                />
                                
                                {message.type === 'ai' && !message.showSuggestions && (
                                    <Tooltip title="Copy">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyMessage(message.text)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                '.MuiPaper-root:hover &': {
                                                    opacity: 0.6
                                                },
                                                '&:hover': { opacity: 1 }
                                            }}
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Paper>

                            {/* Reasoning Section - Collapsible */}
                            {message.type === 'ai' && message.reasoning && (
                                <Box sx={{ mt: 0.5 }}>
                                    {/* Toggle button */}
                                    <Box
                                        role="button"
                                        aria-label={expandedReasoning[message.id] ? 'Hide reasoning' : 'Show reasoning'}
                                        tabIndex={0}
                                        onClick={() => toggleReasoning(message.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleReasoning(message.id); }}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            color: 'text.disabled',
                                            fontSize: '0.7rem',
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            userSelect: 'none',
                                            '&:hover': {
                                                color: AI_COLORS.primary,
                                                bgcolor: 'rgba(139, 92, 246, 0.06)'
                                            },
                                            transition: 'color 0.15s, background-color 0.15s'
                                        }}
                                    >
                                        <PsychologyIcon sx={{ fontSize: 13 }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1 }}>
                                            {expandedReasoning[message.id] ? 'Hide' : 'Show'} reasoning
                                        </Typography>
                                        <ExpandMoreIcon
                                            sx={{
                                                fontSize: 13,
                                                transform: expandedReasoning[message.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s'
                                            }}
                                        />
                                    </Box>

                                    <Collapse in={expandedReasoning[message.id]}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                mt: 0.75,
                                                p: 1.25,
                                                bgcolor: 'rgba(139, 92, 246, 0.04)',
                                                borderRadius: 1.5,
                                                border: '1px solid rgba(139, 92, 246, 0.15)',
                                            }}
                                        >
                                            {/* What the AI understood */}
                                            {message.reasoning.understood && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: AI_COLORS.primaryDark, display: 'block', mb: 0.5, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        Understood
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {message.reasoning.understood.resource && (
                                                            <Chip size="small" label={`resource: ${message.reasoning.understood.resource}`}
                                                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(139,92,246,0.1)', color: AI_COLORS.primaryDark, fontWeight: 500 }} />
                                                        )}
                                                        {message.reasoning.understood.action && (
                                                            <Chip size="small" label={`action: ${message.reasoning.understood.action}`}
                                                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(139,92,246,0.1)', color: AI_COLORS.primaryDark, fontWeight: 500 }} />
                                                        )}
                                                        {message.reasoning.understood.limit && (
                                                            <Chip size="small" label={`limit: ${message.reasoning.understood.limit}`}
                                                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(139,92,246,0.07)', color: 'text.secondary' }} />
                                                        )}
                                                    </Box>
                                                    {message.reasoning.understood.filters && Object.keys(message.reasoning.understood.filters).length > 0 && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                                                Filters: {Object.entries(message.reasoning.understood.filters).map(([k, v]) => {
                                                                    const val = typeof v === 'object' ? v?.value ?? JSON.stringify(v) : v;
                                                                    return `${k}=${val}`;
                                                                }).join(', ')}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            {/* API calls made */}
                                            {message.reasoning.api_calls && message.reasoning.api_calls.length > 0 && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: AI_COLORS.primaryDark, display: 'block', mb: 0.5, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        API Calls
                                                    </Typography>
                                                    {message.reasoning.api_calls.map((call, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                                            <Chip size="small" label={call.method || 'GET'}
                                                                sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 700, minWidth: 36 }} />
                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                                {call.url || ''}
                                                                {call.results_count != null ? ` → ${call.results_count} results` : ''}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Query transformation */}
                                            {message.reasoning.query_transformation && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: AI_COLORS.primaryDark, display: 'block', mb: 0.5, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        Pre-processed
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                                        "{message.reasoning.query_transformation.transformed}"
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Warnings */}
                                            {message.reasoning.warnings && message.reasoning.warnings.length > 0 && (
                                                <Box>
                                                    {message.reasoning.warnings.map((w, idx) => (
                                                        <Typography key={idx} variant="caption" sx={{ fontSize: '0.65rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            ⚠ {w}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}
                                        </Paper>
                                    </Collapse>
                                </Box>
                            )}

                            {!message.showSuggestions && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        px: 1,
                                        fontSize: '0.7rem',
                                        alignSelf: message.type === 'user' ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    {formatTimestamp(message.timestamp)}
                                </Typography>
                            )}
                        </Box>

                        {message.type === 'user' && (
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.grey[600],
                                    width: 36,
                                    height: 36,
                                    mt: 0.5,
                                    boxShadow: '0 2px 8px rgba(100, 116, 139, 0.3)',
                                    border: '2px solid rgba(255, 255, 255, 0.8)'
                                }}
                            >
                                <PersonIcon fontSize="small" />
                            </Avatar>
                        )}
                    </Box>
                ))}

                {/* Thinking indicator - only show when no progress yet */}
                {isProcessing && !currentProgress && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', animation: `${fadeIn} 0.3s ease-out` }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: AI_COLORS.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: `0 4px 12px ${AI_COLORS.shadowStrong}`,
                                border: '2px solid rgba(255, 255, 255, 0.8)'
                            }}
                        >
                            <Box component="img" src="/ai-white.svg" alt="AI" sx={{ width: 22, height: 22 }} />
                        </Box>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: theme.palette.background.paper, borderRadius: '18px 18px 18px 4px', border: `1px solid ${AI_COLORS.border}`, boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CircularProgress size={18} thickness={4} sx={{ color: AI_COLORS.primary }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>Thinking...</Typography>
                        </Paper>
                    </Box>
                )}

                {/* Progress Indicator - when progress starts */}
                {isProcessing && currentProgress && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, animation: `${fadeIn} 0.3s ease-out` }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: AI_COLORS.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: `0 4px 12px ${AI_COLORS.shadowStrong}`,
                                border: '2px solid rgba(255, 255, 255, 0.8)'
                            }}
                        >
                            <Box component="img" src="/ai-white.svg" alt="AI" sx={{ width: 22, height: 22 }} />
                        </Box>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: theme.palette.background.paper,
                                borderRadius: '18px 18px 18px 4px',
                                border: `1px solid ${AI_COLORS.border}`,
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                                maxWidth: '80%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    {currentProgress.message || 'Processing...'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flexGrow: 1, bgcolor: 'rgba(139, 92, 246, 0.15)', borderRadius: 1, height: 4 }}>
                                    <Box
                                        sx={{
                                            bgcolor: AI_COLORS.primary,
                                            height: '100%',
                                            borderRadius: 1,
                                            transition: 'width 0.3s ease',
                                            width: `${(currentProgress.progress || 0) * 100}%`
                                        }}
                                    />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                                    {Math.round((currentProgress.progress || 0) * 100)}%
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Suggestions */}
            {suggestions.length > 0 && messages.length <= 1 && (
                <Box
                    sx={{
                        px: 3,
                        py: 2.5,
                        bgcolor: theme.palette.background.paper,
                        borderTop: `1px solid ${AI_COLORS.border}`,
                        boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.04)'
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {suggestions.slice(0, 4).map((suggestion, index) => (
                            <Chip
                                key={index}
                                label={suggestion}
                                onClick={() => handleSuggestionClick(suggestion)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(139, 92, 246, 0.05)',
                                    border: `1.5px solid ${AI_COLORS.borderMedium}`,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontWeight: 500,
                                    color: AI_COLORS.primaryDark,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        bgcolor: AI_COLORS.primary,
                                        color: theme.palette.common.white,
                                        borderColor: AI_COLORS.primary,
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${AI_COLORS.shadow}`
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Input Area */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                    borderTop: `1px solid ${AI_COLORS.border}`,
                    boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.04)',
                    flexShrink: 0,
                    minWidth: 0
                }}
            >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                    <CustomTextField
                        inputRef={inputRef}
                        multiline
                        maxRows={3}
                        placeholder={hasAIChatPermission ? "Ask me anything..." : "AI chat not enabled for your role"}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        disabled={isProcessing || !hasAIChatPermission}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: theme.spacing(2.25),
                                bgcolor: !hasAIChatPermission ? theme.palette.grey[100] : AI_COLORS.bgSubtle,
                                fontSize: '0.9rem',
                                height: 'auto',
                                minHeight: 'auto',
                                border: `1.5px solid ${!hasAIChatPermission ? theme.palette.grey[300] : AI_COLORS.borderMedium}`,
                                '& fieldset': {
                                    border: 'none'
                                },
                                '&:hover': {
                                    borderColor: !hasAIChatPermission ? theme.palette.grey[300] : AI_COLORS.primary
                                },
                                '&.Mui-focused': {
                                    borderColor: !hasAIChatPermission ? theme.palette.grey[300] : AI_COLORS.primary,
                                    borderWidth: '2px'
                                }
                            }
                        }}
                    />
                    <Tooltip title={!hasAIChatPermission ? "AI chat not enabled for your role" : "Send message"}>
                        <span>
                            <IconButton
                                onClick={handleSendMessage}
                                disabled={!inputValue.trim() || isProcessing || !hasAIChatPermission}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    minWidth: 48,
                                    minHeight: 48,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    background: inputValue.trim() && !isProcessing && hasAIChatPermission
                                        ? AI_COLORS.gradient
                                        : theme.palette.grey[300],
                                    color: theme.palette.common.white,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: inputValue.trim() && !isProcessing && hasAIChatPermission
                                        ? `0 4px 12px ${AI_COLORS.shadow}`
                                        : 'none',
                                    '&:hover': {
                                        background: inputValue.trim() && !isProcessing && hasAIChatPermission
                                            ? AI_COLORS.gradientHover
                                            : theme.palette.grey[300],
                                        transform: inputValue.trim() && !isProcessing && hasAIChatPermission
                                            ? 'scale(1.08)'
                                            : 'none',
                                        boxShadow: inputValue.trim() && !isProcessing && hasAIChatPermission
                                            ? `0 6px 16px ${AI_COLORS.shadowStrong}`
                                            : 'none'
                                    },
                                    '&.Mui-disabled': {
                                        background: theme.palette.grey[300],
                                        color: theme.palette.grey[500]
                                    }
                                }}
                            >
                                <SendIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AIAssistant;
